// ChatBoxMiddle.jsx (Better‑than‑FAANG+ voice memo + rich attachment preview)
// Backward compatible: existing props still work; new features additive.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ClipLoader } from 'react-spinners';
import './chatBoxMiddle.css';

export default function ChatBoxMiddle({
  // Existing props
  isTypingDisplay,
  senderPlayerId,
  status,
  mediaBlobUrl,
  loading,
  handleMicClick,
  isRecording,
  setVoiceMemo,
  clearBlobUrl,
  handleSendAudioMemo,
  fileInput,
  handleFileInput, // legacy: called on <input> change
  error,
  setError,

  // New optional props
  onSendFile, // (file) => Promise|void
  fileSending = false, // parent controls sending state
  // NEW optional flag
  autoClearOnSend = true,
  autoSendOnSelect = false, // if true, immediately calls onSendFile / handleFileInput
  accept = undefined, // override accept types
  maxFileMB = 200, // soft limit (no change to upload logic)
  onFileRejected, // (reason, file)
  showFileSize = true,
  transformFile, // async (file) => newFile (e.g. image compression)
  // NEW: parent message submit (fallback when onSendFile not supplied)
  handleSubmit,
}) {
  const audioRef = useRef(null);

  // Attachment preview state
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [localSending, setLocalSending] = useState(false);
  const prevLoadingRef = useRef(loading);
  const prevFileSendingRef = useRef(fileSending);
  const effectiveSending = fileSending || localSending;

  // ========== Voice Memo Helpers ==========
  const stopRecording = () => {
    // Legacy stop reuses handleMicClick toggler
    handleMicClick?.();
  };

  const cancelVoice = () => {
    try {
      if (isRecording) handleMicClick?.();
    } catch {}
    clearBlobUrl?.();
    setVoiceMemo?.(false);
  };

  const reRecord = () => {
    clearBlobUrl?.();
    setVoiceMemo?.(true);
    if (!isRecording) handleMicClick?.();
  };

  const endRecordOrStart = () => {
    // When recording -> stop; else toggles (used by Stop button)
    handleMicClick?.();
  };

  // ========== File Handling ==========
  const revokePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  };

  useEffect(() => () => revokePreview(), [previewUrl]);

  const buildPreview = (file) => {
    if (!file) return;
    if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return url;
      });
    } else {
      // Non media – no preview URL needed
      setPreviewUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return '';
      });
    }
  };

  const resetFile = () => {
    revokePreview();
    setPreviewUrl('');
    setSelectedFile(null);
    if (fileInput?.current) fileInput.current.value = '';
  };

  const rejectFile = (reason, file) => {
    resetFile();
    onFileRejected?.(reason, file);
    setError?.(reason);
  };

  const processAndSetFile = async (file) => {
    if (!file) return;
    if (file.size / 1024 / 1024 > maxFileMB) {
      rejectFile(`File exceeds ${maxFileMB}MB limit`, file);
      return;
    }
    let eff = file;
    try {
      if (transformFile) {
        const maybeNew = await transformFile(file);
        if (maybeNew instanceof File || maybeNew instanceof Blob) {
          eff = maybeNew;
        }
      }
    } catch (e) {
      // silent; keep original
    }
    setSelectedFile(eff);
    buildPreview(eff);
  };

  const onLocalFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      resetFile();
      handleFileInput?.(e); // legacy pass-through
      return;
    }
    await processAndSetFile(file);

    // Always call legacy callback so previous behavior persists
    handleFileInput?.(e);

    if (autoSendOnSelect) {
      attemptSendFile(file);
    }
  };

  const attemptSendFile = useCallback(
    async (file = selectedFile) => {
      if (!file || effectiveSending) return;
      setError?.('');
      // Path 1: explicit onSendFile prop handles the upload/send
      if (onSendFile) {
        try {
          setLocalSending(true);
          await onSendFile(file);
          resetFile();
        } catch (e) {
          setError?.(e?.message || 'Failed to send file');
        } finally {
          setLocalSending(false);
        }
        return;
      }
      // Path 2: fall back to parent chat message submit (uses parent file state)
      if (handleSubmit) {
        handleSubmit({ type: 'click', meta: 'previewSend' });
      }
      // Do NOT reset here; parent will clear file after successful send
    },
    [selectedFile, onSendFile, effectiveSending, handleSubmit, setError],
  );

  // Auto clear after external send completes (Enter / parent-controlled)
  useEffect(() => {
    if (!autoClearOnSend) {
      prevLoadingRef.current = loading;
      prevFileSendingRef.current = fileSending;
      return;
    }
    const loadingFinished = prevLoadingRef.current && !loading; // transition true -> false
    const fileSendingFinished = prevFileSendingRef.current && !fileSending; // transition true -> false

    if ((loadingFinished || fileSendingFinished) && selectedFile) {
      resetFile();
    }
    prevLoadingRef.current = loading;
    prevFileSendingRef.current = fileSending;
  }, [loading, fileSending, selectedFile, autoClearOnSend]);

  // ========== Typing / Voice Effect ==========
  useEffect(() => {
    if (mediaBlobUrl && audioRef.current) audioRef.current.load();
  }, [mediaBlobUrl]);

  // ========== Derived ==========
  const isImage = !!selectedFile && selectedFile.type.startsWith('image/');
  const isVideo = !!selectedFile && selectedFile.type.startsWith('video/');
  const isGeneric = selectedFile && !isImage && !isVideo;

  return (
    <div className="cbMiddleWrapper">
      {/* Typing Indicator */}
      <div
        className={
          'cbTypingReserve ' +
          (isTypingDisplay && senderPlayerId ? 'hasTyping' : '')
        }
        aria-live="polite"
        aria-hidden={isTypingDisplay ? 'false' : 'true'}
      >
        {isTypingDisplay && senderPlayerId && (
          <div className="cbTyping" role="status" aria-live="polite">
            <span className="cbDot" />
            <span className="cbDot" />
            <span className="cbDot" />
            <span className="cbTypingLabel">{senderPlayerId} typing</span>
          </div>
        )}
      </div>
      {/* Voice Memo Panel */}
      {(isRecording || mediaBlobUrl) && (
        <div
          className={
            'cbVMPanel ' + (isRecording ? 'cbVMRecording' : 'cbVMReview')
          }
        >
          <div className="cbVMLeft">
            {isRecording ? (
              <>
                <span className="cbVMRecDot" aria-label="Recording" />
                <span className="cbVMRecText">
                  Recording…{' '}
                  {status && status !== 'recording' ? `(${status})` : ''}
                </span>
              </>
            ) : (
              <>
                <span className="cbVMReadyIcon">✔</span>
                <span className="cbVMReadyText">Preview</span>
              </>
            )}
          </div>

          <div className="cbVMCenter">
            {mediaBlobUrl && !isRecording && (
              <audio
                ref={audioRef}
                className="cbVMAudio"
                controls
                src={mediaBlobUrl}
                aria-label="Voice memo playback"
              />
            )}
            {isRecording && (
              <div className="cbVMWave" aria-hidden="true">
                <span />
                <span />
                <span />
                <span />
                <span />
              </div>
            )}
          </div>

          <div className="cbVMButtons">
            {isRecording ? (
              <button
                type="button"
                className="cbBtn cbStop"
                onClick={endRecordOrStart}
              >
                Stop
              </button>
            ) : (
              mediaBlobUrl && (
                <button
                  type="button"
                  className="cbBtn cbReRecord"
                  onClick={reRecord}
                >
                  Re‑record
                </button>
              )
            )}
            <button
              type="button"
              className="cbBtn cbCancel"
              onClick={cancelVoice}
            >
              Cancel
            </button>
            {!isRecording && mediaBlobUrl && (
              <button
                type="button"
                className="cbBtn cbSend"
                onClick={handleSendAudioMemo}
              >
                Send
              </button>
            )}
          </div>
        </div>
      )}

      {/* File Preview (new) */}
      {selectedFile && (
        <div
          className="cbFilePreview"
          role="group"
          aria-label="Selected file preview"
        >
          <div className="cbFileMedia">
            {isImage && (
              <img
                src={previewUrl}
                alt={selectedFile.name}
                className="cbFileImg"
                draggable="false"
              />
            )}
            {isVideo && (
              <video
                src={previewUrl}
                className="cbFileVideo"
                controls
                playsInline
              />
            )}
            {isGeneric && (
              <div className="cbFileGeneric">
                <span className="cbFileExt">
                  {selectedFile.name.split('.').pop()?.toUpperCase() || 'FILE'}
                </span>
              </div>
            )}
          </div>
          <div className="cbFileMeta">
            <div className="cbFileName" title={selectedFile.name}>
              {selectedFile.name}
            </div>
            {showFileSize && (
              <div className="cbFileSize">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </div>
            )}
          </div>
          <div className="cbFileActions">
            <button
              type="button"
              className="cbBtn cbFileRemove"
              onClick={resetFile}
              disabled={effectiveSending}
              aria-label="Remove file"
            >
              ✕
            </button>
            {!autoSendOnSelect && (
              <button
                type="button"
                className="cbBtn cbFileSend"
                onClick={() => attemptSendFile()}
                disabled={effectiveSending}
              >
                {effectiveSending ? 'Sending…' : 'Send'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Hidden file input (backward compatible: still calls handleFileInput) */}
      <input
        ref={fileInput}
        type="file"
        hidden
        onChange={onLocalFileChange}
        accept={accept}
        aria-hidden="true"
      />

      {/* Legacy loader */}
      {/* {loading && (
        <div className="cbLoading">
          <ClipLoader size={16} color="#1e7bff" /> <span>Uploading…</span>
        </div>
      )} */}

      {error && (
        <div className="cbError" role="alert">
          {error}
          <button
            type="button"
            className="cbErrClose"
            onClick={() => setError?.('')}
            aria-label="Dismiss error"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
